import React, { memo, useCallback, useMemo } from 'react';
import cx from 'classnames';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { Card, CardHeader, CardBody } from 'reactstrap';
import { Icon } from 'js/components';
import { getQuestionSetCategory } from 'js/utils/question-set';
import OrgTrendsChart from './OrgTrendsChart';
import Question from './Question';
import { getTrendSeriesForChart } from 'js/utils/org-trends';
import { buildOrgTrendsChartLines } from 'js/utils/org-trends-rollup';
import { createOrgAssessmentWeightContext } from 'js/utils/org-assessment-rollup';
import { useOrgTrends } from './OrgTrendsContext';

import styles from './OrgTrends.module.scss';

function QuestionSet({
  questionSet,
  teams,
  window,
  expanded,
  onToggle,
  lineHoverMaxDistance,
  allowQuestionCollapse = true,
}) {
  const {
    organization,
    selectedPickerRow,
    categoryLookups,
    showCategoryPills,
    debounceTrendPoints,
  } = useOrgTrends();

  const category = useMemo(
    () => getQuestionSetCategory(questionSet),
    [questionSet]
  );

  const categoryInfo = categoryLookups.get(category);
  const isUncategorized = category === 'uncategorized';
  const questionCount = questionSet.questions?.length ?? 0;

  const weightContext = useMemo(
    () => createOrgAssessmentWeightContext(organization),
    [organization]
  );

  const setLines = useMemo(
    () =>
      buildOrgTrendsChartLines({
        organization,
        teams,
        weightContext,
        selectedPickerRow,
        getTeamSeries: (series) => series?.questionSets?.[questionSet.key],
        getTrendSeriesForChart,
        window,
        debounceTrendPoints,
      }),
    [
      debounceTrendPoints,
      organization,
      questionSet.key,
      selectedPickerRow,
      teams,
      weightContext,
      window,
    ]
  );

  const handleToggleExpanded = useCallback(() => {
    onToggle(questionSet.key);
  }, [onToggle, questionSet.key]);

  const showQuestions = allowQuestionCollapse ? expanded : true;

  return (
    <Card className={styles['question-set']}>
      <CardHeader>
        <div className={styles['section-header']}>
          <div className={styles['section-header-main']}>
            <span className={styles['question-set-name']}>
              {questionSet.name}
            </span>
            {questionSet.retired && (
              <span className={styles.retiredBadge}>Retired</span>
            )}
          </div>
          {showCategoryPills && categoryInfo && (
            <span
              className={cx(styles.categoryBadge, {
                [styles.categoryBadgeUncategorized]: isUncategorized,
                [categoryInfo.palette]: !isUncategorized,
              })}
            >
              {categoryInfo.display_name}
            </span>
          )}
        </div>
      </CardHeader>
      <CardBody>
        <div
          className={`${styles['chart-wrapper-large']} ${styles['question-set-chart']}`}
        >
          <OrgTrendsChart
            window={window}
            trendLines={setLines}
            lineHoverMaxDistance={lineHoverMaxDistance}
          />
        </div>
        {allowQuestionCollapse && (
          <div className={styles['question-set-footer']}>
            <button
              type="button"
              className={cx(styles.expander, {
                [styles.collapsed]: !expanded,
              })}
              onClick={handleToggleExpanded}
              aria-expanded={expanded}
              aria-label={expanded ? 'Hide questions' : 'Show questions'}
            >
              <Icon icon={faChevronDown} />
            </button>
            {!expanded && (
              <span className={styles.questionCount}>
                {`${questionCount} ${
                  questionCount === 1 ? 'question' : 'questions'
                }`}
              </span>
            )}
          </div>
        )}
        {showQuestions &&
          questionSet.questions.map((question) => (
            <Question
              key={`${questionSet.key}|${question.key}`}
              label={question.text}
              retired={question.retired}
              teams={teams}
              window={window}
              seriesKey={`${questionSet.key}|${question.key}`}
              lineHoverMaxDistance={lineHoverMaxDistance}
            />
          ))}
      </CardBody>
    </Card>
  );
}

export default memo(QuestionSet);
